/**
 * @author Harish.R <harish.r@314ecorp.com>
 * constants for claim status response
 */
import _ from 'lodash';
import dateParser from 'lib/dateParser';

import { CLAIM_STATUS_CODES } from 'app-constants/claims/claimStatusCodes';
import { MONTH_DATE_YEAR_SLASH_FORMAT } from 'app-constants/index';
import { PROVIDER_IDENTIFIERS_FORM_FIELDS } from 'app-constants/eligibility';

const CLAIM_STATUS_PATIENT_SEARCH_TYPE: EligibilityRequests.IPatientSearchType = 'claim_status';

const CLAIM_AGE_OPTIONS_IN_DAYS = [30, 60, 90, 120];

const CLAIMS_UNAVAILABLE_PAYER_LABEL = 'Payers not available for Claim Status';

const CLAIM_STATUS_FORM_FIELDS: Record<
	keyof ClaimStatusRequestForm.IFormModel,
	{ title: string; placeholder: string; errorMessage: string; info?: string }
> = {
	providerId: {
		title: 'Provider/Organization',
		placeholder: 'Select a Provider/Organization',
		errorMessage: 'Please select a Provider/Organization',
		info: 'Choose a Provider/Organization from the dropdown to make the request.',
	},
	locationId: {
		title: 'Service Location',
		placeholder: 'Select a Service Location',
		errorMessage: 'Please select a Service Location',
	},
	payerId: {
		title: 'Payer',
		placeholder: 'Select a Payer',
		errorMessage: 'Please select a Payer',
		info: 'Please select the payer to which you are submitting the transaction.',
	},
	relationWithSubscriber: {
		title: 'Relationship to the Subscriber',
		placeholder: 'Self',
		errorMessage: 'Please select a Relationship',
	},
	memberIdOfPatient: {
		title: 'Member ID',
		placeholder: 'E.g.: WXI234FJCHI',
		errorMessage: 'Please enter a valid Member ID',
	},
	accountNumberOfPatient: {
		title: 'Account Number',
		placeholder: 'E.g.: WXI234FJCHI',
		errorMessage: 'Please enter a valid Account Number',
	},
	firstNameOfPatient: {
		title: 'First Name',
		placeholder: 'E.g.: John',
		errorMessage: 'Please enter the First Name',
	},
	lastNameOfPatient: {
		title: 'Last Name',
		placeholder: 'E.g.: Doe',
		errorMessage: 'Please enter the Last Name',
	},
	dateOfBirthOfPatient: {
		title: 'Date of Birth',
		placeholder: MONTH_DATE_YEAR_SLASH_FORMAT,
		errorMessage: 'Please enter the Date of Birth',
	},
	genderOfPatient: {
		title: 'Gender',
		placeholder: 'Select a Gender',
		errorMessage: 'Please select a Gender',
	},
	firstNameOfSubscriber: {
		title: 'First Name',
		placeholder: 'E.g.: John',
		errorMessage: 'Please enter the First Name',
	},
	lastNameOfSubscriber: {
		title: 'Last Name',
		placeholder: 'E.g.: Doe',
		errorMessage: 'Please enter the Last Name',
	},
	serviceStartDate: {
		title: 'Service Start Date',
		placeholder: dateParser().startOf('day').format(MONTH_DATE_YEAR_SLASH_FORMAT),
		errorMessage: 'Please enter the service start date',
		info: 'Enter the date on which the service starts.',
	},
	serviceEndDate: {
		title: 'Service End Date',
		placeholder: MONTH_DATE_YEAR_SLASH_FORMAT,
		errorMessage: 'Please enter the service end date',
		info: 'Enter the date on which the service ends. ',
	},
	claimNumber: {
		title: 'Claim Number',
		placeholder: 'E.g.: 2223456E7390',
		errorMessage: 'Please enter the claim number',
		info: 'This is the payer-assigned number for professional, facility, or dental claims. It helps identify the specific claim in claim status results.',
	},
	claimAmount: {
		title: 'Claim Amount (USD)',
		placeholder: 'Enter the Claim Amount',
		errorMessage: 'Please enter the claim amount',
	},
	patientReqFields: {
		title: 'Search Patient by',
		placeholder: 'Select a combination',
		errorMessage: 'Please select a combination',
	},
	providerReqFields: {
		title: 'Search Provider by',
		placeholder: 'Select a combination',
		errorMessage: 'Please select a combination',
	},
	...PROVIDER_IDENTIFIERS_FORM_FIELDS,
};

const REQUIRED_FIELD_MESSAGES: Record<keyof ClaimStatusRequestForm.IFormModel, string> = _.mapValues(
	CLAIM_STATUS_FORM_FIELDS,
	(field) => field.errorMessage,
);

const CLAIM_STATUS_LIST_VIEW_FIELDS = {
	simpleSearch: {
		placeholder: 'Search by Patient Name',
	},
	patientName: {
		label: 'Patient Name',
		placeholder: 'E.g.: John Doe',
	},
	identifier: {
		label: 'Patient Identifier ',
		placeholder: CLAIM_STATUS_FORM_FIELDS.memberIdOfPatient.placeholder,
		tooltip: 'This is an identifier unique to the patient such as Member ID, SSN, Group Number or Medicaid ID.',
	},
	payer: {
		label: 'Payer',
		placeholder: 'Select a Payer',
		selectAllLabel: 'All Payers',
	},
	dateOfService: {
		label: 'Service Dates',
	},
	claimNumber: {
		label: 'Claim Number',
		placeholder: 'E.g.: 2223456E7390',
	},
	dob: {
		label: 'Date of Birth',
		placeholder: MONTH_DATE_YEAR_SLASH_FORMAT,
	},
	advancedSearchButtons: { reset: 'Clear', submit: 'Search' },

	// filter section
	provider: {
		label: 'Providers/Org',
		placeholder: 'Select the Providers/Organization',
		selectAllLabel: 'All Providers/Organization',
	},
	claimStatus: {
		label: 'Status',
		placeholder: 'Select the Claim Statuses',
		selectAllLabel: 'All Statuses',
		tooltip: 'Choose the claim statuses by which you wish to view the previous responses.',
	},
	claimAmount: {
		label: 'Billed Amount',
		placeholder: 'E.g: 49.9',
	},
	claimAge: {
		label: 'Claim Age',
		placeholder: 'E.g.: 30',
	},
	claimId: {
		label: 'Claim ID',
		placeholder: 'E.g.: 2223456E7390',
	},
	sort: {
		label: 'Sort By',
		placeholder: 'Select the option to sort',
	},
	filterLocation: {
		label: 'Filter by Service Location ',
		placeholder: 'Select the Service Locations',
		selectAllLabel: 'All Service Locations',
	},
	filterButtons: { reset: 'Clear', apply: 'Apply' },
};

const CLAIM_STATUS_TAG_COLORS = _.keyBy(CLAIM_STATUS_CODES, (item) => item.code);

const CLAIM_STATUS_MULTISELECT_OPTIONS: IMultiSelectOption[] = _.map(
	_.sortBy(
		_.filter(CLAIM_STATUS_CODES, (item) => !!item.order),
		(item) => item.order,
	),
	(item) => ({
		value: item.label,
		label: item.label,
		color: item.color,
	}),
);

const CLAIM_STATUS_SORT_OPTIONS: ClaimStatusRequests.ISortOptions[] = [
	{
		label: 'Claim Age - Old to New',
		value: 'claimAgeDesc',
		key: 'claimAgeDesc',
		param: [{ column: 'claimAge', asc: false }],
	},
	{
		label: 'Claim Age - New to Old',
		value: 'claimAgeAsc',
		key: 'claimAgeAsc',
		param: [{ column: 'claimAge', asc: true }],
	},
	{
		label: 'Claim Amount - Low to High',
		value: 'claimAmountAsc',
		key: 'claimAmountAsc',
		param: [{ column: 'claimAmount', asc: true }],
	},
	{
		label: 'Claim Amount - High to Low',
		value: 'claimAmountDesc',
		key: 'claimAmountDesc',
		param: [{ column: 'claimAmount', asc: false }],
	},
];
/**
 * claim status label v/s codes list mapping
 */
const CLAIM_STATUS_CODES_MAPPING = _.mapValues(
	_.groupBy(CLAIM_STATUS_CODES, ({ label }) => label),
	(list) => _.map(list, (item) => item.code),
);

const CLAIM_STATUS_CODES_FOR_AGING = [
	'Pending',
	'Additional Information Required',
	'Acknowledged - Accepted for Processing',
	'Acknowledged - Received',
	'Acknowledged - Forwarded',
	'Acknowledged - Accepted with Warning',
	'Acknowledged - Split',
];

const ERRORED_CLAIM_STATUS_CODES = _.flatMap(_.pick(CLAIM_STATUS_CODES_MAPPING, ['Implementation Error', 'Input Error', 'Network Error']));
const INPUT_ERRORED_CLAIM_STATUS_CODES = _.flatMap(_.pick(CLAIM_STATUS_CODES_MAPPING, ['Input Error']));

const CLAIMS_MODAL_CONTENT = 'This payer is available only for Claim Status Checks.';

export {
	CLAIM_STATUS_PATIENT_SEARCH_TYPE,
	CLAIM_STATUS_FORM_FIELDS,
	CLAIM_STATUS_LIST_VIEW_FIELDS,
	CLAIM_STATUS_TAG_COLORS,
	CLAIM_STATUS_MULTISELECT_OPTIONS,
	CLAIM_STATUS_CODES_FOR_AGING,
	CLAIM_STATUS_CODES_MAPPING,
	ERRORED_CLAIM_STATUS_CODES,
	CLAIM_AGE_OPTIONS_IN_DAYS,
	CLAIMS_MODAL_CONTENT,
	CLAIMS_UNAVAILABLE_PAYER_LABEL,
	REQUIRED_FIELD_MESSAGES,
	INPUT_ERRORED_CLAIM_STATUS_CODES,
	CLAIM_STATUS_SORT_OPTIONS,
};
